/**
 * The condition builder's model — pure helpers over an array of
 * `{ column, op, value }` filters, kept out of the control so it can be unit
 * tested without rendering. Mirrors the server's `DatasetFilterOp` /
 * `DatasetQuerySpec` (the two cannot share a type — client code does not import
 * server code — so the op list is duplicated and must stay in step).
 */

export type DatasetFilterOp =
  | 'eq'
  | 'neq'
  | 'contains'
  | 'gt'
  | 'lt'
  | 'gte'
  | 'lte'
  | 'is_null'
  | 'is_not_null';

export type DatasetCondition = {
  readonly column: string;
  readonly op: DatasetFilterOp;
  readonly value: string;
};

/** Operators, with their label and whether they take a value. */
export const DATASET_FILTER_OP_OPTIONS: readonly {
  readonly op: DatasetFilterOp;
  readonly label: string;
  readonly takesValue: boolean;
}[] = [
  { op: 'eq', label: 'equals', takesValue: true },
  { op: 'neq', label: 'does not equal', takesValue: true },
  { op: 'contains', label: 'contains', takesValue: true },
  { op: 'gt', label: 'greater than', takesValue: true },
  { op: 'lt', label: 'less than', takesValue: true },
  { op: 'gte', label: 'at least', takesValue: true },
  { op: 'lte', label: 'at most', takesValue: true },
  { op: 'is_null', label: 'is empty', takesValue: false },
  { op: 'is_not_null', label: 'is not empty', takesValue: false },
];

const OP_SET = new Set<string>(DATASET_FILTER_OP_OPTIONS.map((o) => o.op));

export const datasetFilterOpTakesValue = (op: DatasetFilterOp): boolean =>
  DATASET_FILTER_OP_OPTIONS.find((o) => o.op === op)?.takesValue ?? true;

/** Reads the saved param back to conditions, tolerating a partly-typed value. */
export const parseConditions = (raw: unknown): DatasetCondition[] => {
  if (!Array.isArray(raw)) {
    return [];
  }
  const out: DatasetCondition[] = [];
  for (const item of raw) {
    if (typeof item !== 'object' || item === null) {
      continue;
    }
    const record = item as Record<string, unknown>;
    const column = typeof record['column'] === 'string' ? record['column'] : '';
    const op =
      typeof record['op'] === 'string' && OP_SET.has(record['op'])
        ? (record['op'] as DatasetFilterOp)
        : 'eq';
    const value = typeof record['value'] === 'string' ? record['value'] : '';
    out.push({ column, op, value });
  }
  return out;
};

/**
 * Serializes conditions to the param value: `{ column, op, value }[]`, with
 * `value` null for the valueless operators. `undefined` when the list is empty,
 * so an empty builder clears the param rather than saving `[]`.
 */
export const serializeConditions = (
  list: readonly DatasetCondition[]
): unknown => {
  if (list.length === 0) {
    return undefined;
  }
  return list.map((condition) => ({
    column: condition.column,
    op: condition.op,
    value: datasetFilterOpTakesValue(condition.op) ? condition.value : null,
  }));
};

export const ConditionOps = {
  add: (list: readonly DatasetCondition[]): DatasetCondition[] => [
    ...list,
    { column: '', op: 'eq', value: '' },
  ],
  removeAt: (
    list: readonly DatasetCondition[],
    index: number
  ): DatasetCondition[] => list.filter((_, i) => i !== index),
  setColumn: (
    list: readonly DatasetCondition[],
    index: number,
    column: string
  ): DatasetCondition[] =>
    list.map((condition, i) =>
      i === index ? { ...condition, column } : condition
    ),
  setOp: (
    list: readonly DatasetCondition[],
    index: number,
    op: DatasetFilterOp
  ): DatasetCondition[] =>
    list.map((condition, i) =>
      i === index ? { ...condition, op } : condition
    ),
  setValue: (
    list: readonly DatasetCondition[],
    index: number,
    value: string
  ): DatasetCondition[] =>
    list.map((condition, i) =>
      i === index ? { ...condition, value } : condition
    ),
};
